// Kurum ticari ekranları: önerilen rotalar (B14–B16), faturalar (B23–B24): Zod girdileri ve yanıt tipleri (API ve konsol aynı tipi kullanır).
// Uçlar kiracıdan bağımsızdır: servis firması tarafı (C42–C45 teklif, C55–C57 faturalama) Faz 10'da aynı tipleri kullanır.
import { z } from 'zod';
import type { Direction } from '../enums';
import { STOP_KINDS, type StopKind } from './planning';
import type { Paged } from './common';

const PageFields = {
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(50),
};

export interface CommercialParty {
  id: string;
  name: string;
}

// ───────── teklif durumları ─────────

export const PROPOSAL_STATUSES = ['DRAFT', 'SHARED', 'CHANGES_REQUESTED', 'APPROVED', 'REJECTED'] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];
export const PROPOSAL_STATUS_LABELS: Record<ProposalStatus, string> = {
  DRAFT: 'Taslak',
  SHARED: 'İncelemede',
  CHANGES_REQUESTED: 'Değişiklik istendi',
  APPROVED: 'Onaylandı',
  REJECTED: 'Reddedildi',
};
/** Kararı bekleyen (açık) durumlar. Taslak yalnız servis firmasına görünür. */
export const PROPOSAL_OPEN_STATUSES: readonly ProposalStatus[] = ['SHARED', 'CHANGES_REQUESTED'];
/** Karar verilmiş teklife yorum eklenmez, karar değişmez. */
export const PROPOSAL_FINAL_STATUSES: readonly ProposalStatus[] = ['APPROVED', 'REJECTED'];

export const PROPOSAL_FILTERS = [
  'open',
  'SHARED',
  'CHANGES_REQUESTED',
  'APPROVED',
  'REJECTED',
  'DRAFT',
  'all',
] as const;
export type ProposalFilter = (typeof PROPOSAL_FILTERS)[number];

export const ProposalListQuery = z.object({
  status: z.enum(PROPOSAL_FILTERS).default('all'),
  institutionId: z.string().max(64).optional(),
  q: z.string().trim().max(80).optional(),
  ...PageFields,
});
export type ProposalListQuery = z.input<typeof ProposalListQuery>;

// ───────── teklif ölçütleri (mevcut vs önerilen) ─────────

/** Teklif özet ölçütleri. `monthlyCost` kuruş; `occupancy` yüzde; `walkM` ortalama yürüme (m). */
export const ProposalMetricsSchema = z.object({
  vehicles: z.number().int().min(0).max(10_000),
  km: z.number().min(0),
  avgMinutes: z.number().min(0),
  monthlyCost: z.number().int().min(0),
  walkM: z.number().min(0),
  occupancy: z.number().min(0).max(100).optional(),
});
export type ProposalMetrics = z.infer<typeof ProposalMetricsSchema>;
export type ProposalMetricKey = keyof ProposalMetrics;
export type ProposalMetricUnit = 'count' | 'km' | 'min' | 'money' | 'm' | 'pct';

export const PROPOSAL_METRICS: {
  key: ProposalMetricKey;
  label: string;
  short: string;
  unit: ProposalMetricUnit;
  better: 'lower' | 'higher';
}[] = [
  { key: 'vehicles', label: 'Araç sayısı', short: 'Araç sayısı', unit: 'count', better: 'lower' },
  { key: 'km', label: 'Toplam km', short: 'Toplam km', unit: 'km', better: 'lower' },
  { key: 'avgMinutes', label: 'Ortalama yolculuk süresi', short: 'Ort. yolculuk', unit: 'min', better: 'lower' },
  { key: 'monthlyCost', label: 'Aylık maliyet', short: 'Aylık maliyet', unit: 'money', better: 'lower' },
  { key: 'walkM', label: 'Ortalama yürüme mesafesi', short: 'Ort. yürüme', unit: 'm', better: 'lower' },
  { key: 'occupancy', label: 'Doluluk', short: 'Doluluk', unit: 'pct', better: 'higher' },
];

export interface ProposalMetricDelta {
  key: ProposalMetricKey;
  label: string;
  short: string;
  unit: ProposalMetricUnit;
  current: number | null;
  proposed: number | null;
  /** önerilen − mevcut */
  delta: number | null;
  /** yüzde değişim (mevcut 0 veya yoksa null) */
  pct: number | null;
  /** iyileşme: düşük-iyi ölçütte azalma, yüksek-iyi ölçütte artış */
  trend: 'good' | 'bad' | 'neutral';
}

/** Mevcut ve önerilen ölçütlerden fark satırları (tanımsız ölçüt atlanmaz; değer null kalır). */
export function proposalDeltas(
  current: Partial<ProposalMetrics> | null | undefined,
  proposed: Partial<ProposalMetrics> | null | undefined,
): ProposalMetricDelta[] {
  return PROPOSAL_METRICS.map((m) => {
    const c = typeof current?.[m.key] === 'number' ? current[m.key]! : null;
    const p = typeof proposed?.[m.key] === 'number' ? proposed[m.key]! : null;
    const delta = c !== null && p !== null ? p - c : null;
    const pct = delta !== null && c ? (delta / c) * 100 : null;
    const trend: ProposalMetricDelta['trend'] =
      delta === null || Math.abs(delta) < 1e-9 ? 'neutral' : delta < 0 === (m.better === 'lower') ? 'good' : 'bad';
    return { key: m.key, label: m.label, short: m.short, unit: m.unit, current: c, proposed: p, delta, pct, trend };
  });
}

// ───────── önerilen rotalar (RouteProposal.routes JSON / OptimizationScenario.routes) ─────────

export const PROPOSED_ROUTE_CHANGES = ['NEW', 'CHANGED', 'MERGED', 'UNCHANGED'] as const;
export type ProposedRouteChange = (typeof PROPOSED_ROUTE_CHANGES)[number];
/** Karşılaştırmadaki rota durumu: mevcut rota kaldırılabilir; çizimsiz teklifte yalnız "adı geçiyor". */
export type CompareRouteChange = ProposedRouteChange | 'REMOVED' | 'MENTIONED';
export const ROUTE_CHANGE_LABELS: Record<CompareRouteChange, string> = {
  NEW: 'Yeni hat',
  CHANGED: 'Güzergâh değişti',
  MERGED: 'Birleştirildi',
  UNCHANGED: 'Değişmedi',
  REMOVED: 'Kaldırıldı',
  MENTIONED: 'Teklifte geçiyor',
};

export const ProposedStopSchema = z.object({
  name: z.string().trim().min(1).max(120),
  lat: z.number().min(-90).max(90),
  lng: z.number().min(-180).max(180),
  /** gidiş yönünde sefer başından saniye (yolculuk süresi etkisi için) */
  offsetS: z.number().int().min(0).optional(),
  kind: z.enum(STOP_KINDS).optional(),
});
export type ProposedStop = z.infer<typeof ProposedStopSchema>;

export const ProposedRouteSchema = z.object({
  code: z.string().trim().min(1).max(16),
  name: z.string().trim().min(1).max(120),
  change: z.enum(PROPOSED_ROUTE_CHANGES).default('CHANGED'),
  /** bu hattın yerini aldığı mevcut rota kodları (birleştirme / yeniden çizim) */
  replaces: z.array(z.string().trim().max(16)).max(12).default([]),
  /** Google encoded polyline (1e5) */
  polyline: z.string().min(2).max(200_000),
  distanceM: z.number().int().min(0),
  durationS: z.number().int().min(0),
  vehicles: z.number().int().min(1).max(20).default(1),
  stops: z.array(ProposedStopSchema).min(2).max(60),
});
export type ProposedRoute = z.infer<typeof ProposedRouteSchema>;

/**
 * Teklifin rota belgesi. `routes` yalnız değişen/yeni hatları taşır; listede olmayan mevcut rotalar "değişmedi" sayılır.
 * `removed`: tamamen kaldırılan mevcut rota kodları. Çizimsiz teklif yalnız `note` taşıyabilir.
 */
export const ProposalRoutesDocSchema = z.object({
  note: z.string().trim().max(1000).optional(),
  routes: z.array(ProposedRouteSchema).max(80).optional(),
  removed: z.array(z.string().trim().max(16)).max(80).optional(),
});
export type ProposalRoutesDoc = z.infer<typeof ProposalRoutesDocSchema>;

// ───────── yorumlar ve karar ─────────

export const ProposalCommentInput = z
  .object({
    body: z.string().trim().min(2, 'Yorumu en az 2 harfle yazın.').max(1000, 'Yorum en fazla 1000 karakter olabilir.'),
    lat: z.number().min(-90).max(90).optional(),
    lng: z.number().min(-180).max(180).optional(),
    routeCode: z.string().trim().min(1).max(16).optional(),
  })
  .refine((v) => (v.lat === undefined) === (v.lng === undefined), {
    message: 'Haritadaki nokta için enlem ve boylam birlikte gönderilmeli.',
    path: ['lat'],
  });
export type ProposalCommentInput = z.input<typeof ProposalCommentInput>;

export const ProposalCommentUpdateInput = z.object({ resolved: z.boolean() });
export type ProposalCommentUpdateInput = z.infer<typeof ProposalCommentUpdateInput>;

export type ProposalSide = 'PROVIDER' | 'INSTITUTION' | 'PLATFORM';

export interface ProposalCommentItem {
  id: string;
  body: string;
  author: { name: string; company: string; side: ProposalSide; mine: boolean };
  /** haritaya iğnelenmiş yorum */
  pin: { lat: number; lng: number } | null;
  routeCode: string | null;
  resolvedAt: string | null;
  createdAt: string;
}

export const PROPOSAL_DECISIONS = ['APPROVE', 'REQUEST_CHANGES', 'REJECT'] as const;
export type ProposalDecision = (typeof PROPOSAL_DECISIONS)[number];
export const PROPOSAL_DECISION_STATUS: Record<ProposalDecision, ProposalStatus> = {
  APPROVE: 'APPROVED',
  REQUEST_CHANGES: 'CHANGES_REQUESTED',
  REJECT: 'REJECTED',
};
export const PROPOSAL_DECISION_LABELS: Record<ProposalDecision, string> = {
  APPROVE: 'Onayla',
  REQUEST_CHANGES: 'Değişiklik iste',
  REJECT: 'Reddet',
};
/** Kararın verilebildiği durumlar (değişiklik istendikten sonra da onay/ret verilebilir). */
export const PROPOSAL_DECISION_FROM: Record<ProposalDecision, readonly ProposalStatus[]> = {
  APPROVE: ['SHARED', 'CHANGES_REQUESTED'],
  REQUEST_CHANGES: ['SHARED'],
  REJECT: ['SHARED', 'CHANGES_REQUESTED'],
};

export const ProposalDecisionInput = z.discriminatedUnion('decision', [
  z.object({ decision: z.literal('APPROVE'), note: z.string().trim().max(500).optional() }),
  z.object({
    decision: z.literal('REQUEST_CHANGES'),
    reason: z
      .string()
      .trim()
      .min(10, 'İstediğiniz değişikliği en az 10 harfle yazın.')
      .max(1000, 'Açıklama en fazla 1000 karakter olabilir.'),
  }),
  z.object({
    decision: z.literal('REJECT'),
    reason: z
      .string()
      .trim()
      .min(10, 'Ret gerekçesini en az 10 harfle yazın.')
      .max(1000, 'Açıklama en fazla 1000 karakter olabilir.'),
  }),
]);
export type ProposalDecisionInput = z.infer<typeof ProposalDecisionInput>;

// ───────── teklif listesi ve detayı (B14/B16, C42) ─────────

export interface ProposalRow {
  id: string;
  title: string;
  status: ProposalStatus;
  provider: CommercialParty;
  institution: CommercialParty;
  createdAt: string;
  decidedAt: string | null;
  current: Partial<ProposalMetrics>;
  proposed: Partial<ProposalMetrics>;
  /** teklifin dokunduğu mevcut/yeni rota kodları */
  routeCodes: string[];
  commentCount: number;
  openCommentCount: number;
  /** önerilen güzergâh çizimi var mı (yoksa karşılaştırma yalnız ölçüt ve nottan oluşur) */
  hasGeometry: boolean;
  /** teklifin geçerlilik günü (YYYY-MM-DD; yoksa null) */
  validUntil: string | null;
  /** kuruma gönderilen sürüm (0 = henüz gönderilmedi) */
  revision: number;
  /** son işlem: oluşturma, gönderim, güncelleme, karar veya yorum */
  lastActivityAt: string;
}

export interface ProposalList extends Paged<ProposalRow> {
  counts: {
    open: number;
    shared: number;
    changesRequested: number;
    approved: number;
    rejected: number;
    draft: number;
    all: number;
  };
  /** onaylanan tekliflerin aylık maliyet farkı toplamı (kuruş; eksi = tasarruf) */
  approvedMonthlyDelta: number;
}

export type ProposalEventKind = 'CREATED' | 'SHARED' | 'REVISED' | 'CHANGES_REQUESTED' | 'APPROVED' | 'REJECTED';

export interface ProposalEvent {
  at: string;
  kind: ProposalEventKind;
  title: string;
  /** "Ad Soyad · Firma" */
  actor: string | null;
  detail: string | null;
}

export interface ProposalRouteRef {
  code: string;
  name: string;
  change: CompareRouteChange;
}

export interface ProposalDetail extends ProposalRow {
  note: string | null;
  decision: { status: ProposalStatus; by: string | null; at: string; reason: string | null } | null;
  comments: ProposalCommentItem[];
  events: ProposalEvent[];
  /** yorum için rota seçimi ve haritada gösterim */
  routes: ProposalRouteRef[];
  viewer: { side: ProposalSide; canComment: boolean; canDecide: boolean };
}

// ───────── karşılaştırma (B15, C43) ─────────

export interface CompareStop {
  name: string;
  lat: number;
  lng: number;
  kind: StopKind;
  offsetS: number | null;
}

export interface CompareRoute {
  code: string;
  name: string;
  change: CompareRouteChange;
  replaces: string[];
  polyline: string;
  distanceM: number;
  durationS: number;
  vehicles: number;
  stops: CompareStop[];
  /** mevcut rotada atanmış yolcu sayısı (önerilende null) */
  passengers: number | null;
}

export interface CompareStopChange {
  name: string;
  routeCode: string;
  /** taşınan durakta mesafe (m) */
  movedM?: number;
}

export interface ProposalStopChanges {
  added: CompareStopChange[];
  removed: CompareStopChange[];
  moved: CompareStopChange[];
  unchanged: number;
}

/** Yolcu etkisi — yalnız sayılar; kişisel veri yok. */
export interface ProposalImpact {
  /** değişen rotalardaki yolcu */
  affected: number;
  /** değişmeyen rotalardaki yolcu */
  unaffected: number;
  sameStop: number;
  movedNear: number;
  movedFar: number;
  rideLonger: number;
  rideShorter: number;
  rideSimilar: number;
  /** durağı taşınanlarda ortalama taşınma (m) */
  avgMoveM: number | null;
  thresholds: { sameStopM: number; nearM: number; rideMin: number };
}

export interface ProposalCompare {
  id: string;
  title: string;
  status: ProposalStatus;
  provider: CommercialParty;
  institution: CommercialParty;
  hasGeometry: boolean;
  note: string | null;
  routeCodes: string[];
  current: CompareRoute[];
  proposed: CompareRoute[];
  metrics: { current: Partial<ProposalMetrics>; proposed: Partial<ProposalMetrics> };
  stopChanges: ProposalStopChanges | null;
  impact: ProposalImpact | null;
}

// ───────── servis firması teklif çalışma alanı (C42–C45) ─────────
// Şema dondurulduğu için ek bilgiler teklif rota belgesinde (RouteProposal.routes JSON) ayrı anahtarlarda saklanır:
// `meta` (iki tarafa görünür: geçerlilik, sürüm, kaynak) ve `internal` (yalnız servis firması: maliyet varsayımları,
// fiyat). `ProposalRoutesDocSchema` bu anahtarları tanımaz → kurum/paylaşım yanıtlarına hiçbir zaman çıkmaz.

export const PROPOSAL_SOURCES = ['CURRENT', 'SCENARIO'] as const;
export type ProposalSource = (typeof PROPOSAL_SOURCES)[number];
export const PROPOSAL_SOURCE_LABELS: Record<ProposalSource, string> = {
  CURRENT: 'Mevcut plandan',
  SCENARIO: 'Optimizasyon senaryosundan',
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const DateInput = z.string().regex(DATE_RE, 'Tarihi seçin (GG.AA.YYYY).');
const RouteCode = z
  .string()
  .trim()
  .min(1, 'Rota kodu yazın.')
  .max(16, 'Rota kodu en fazla 16 karakter olabilir.')
  .regex(/^[A-Za-z0-9ÇĞİÖŞÜçğıöşü-]+$/, 'Rota kodunda yalnız harf, rakam ve tire kullanın.')
  .transform((s) => s.toLocaleUpperCase('tr'));

/** Teklifin iki tarafa görünen bilgileri (rota belgesinde `meta`). */
export const ProposalMetaSchema = z.object({
  contractId: z.string().max(64).nullish(),
  source: z.enum(PROPOSAL_SOURCES).default('CURRENT'),
  scenarioId: z.string().max(64).nullish(),
  validUntil: z.string().regex(DATE_RE).nullish(),
  revision: z.number().int().min(0).default(0),
});
export type ProposalMeta = z.infer<typeof ProposalMetaSchema>;

/** Servis firmasının iç maliyet varsayımları (kuruş). Kuruma ve paylaşım bağlantısına hiçbir zaman gönderilmez. */
export const ProposalCostAssumptionsSchema = z.object({
  /** araç başı aylık sabit maliyet: şoför, kira/amortisman, sigorta */
  vehicleMonthly: z
    .number()
    .int()
    .min(0, 'Araç maliyeti eksi olamaz.')
    .max(100_000_000, 'Araç maliyeti çok yüksek görünüyor.'),
  /** km başı değişken maliyet: yakıt, bakım, lastik */
  perKm: z.number().int().min(0, 'Km maliyeti eksi olamaz.').max(1_000_000, 'Km maliyeti çok yüksek görünüyor.'),
  /** genel gider payı (%) */
  overheadPct: z.number().min(0, 'Genel gider payı eksi olamaz.').max(60, 'Genel gider payı en fazla %60 olabilir.'),
});
export type ProposalCostAssumptions = z.infer<typeof ProposalCostAssumptionsSchema>;
export const ProposalCostsInput = ProposalCostAssumptionsSchema;
export type ProposalCostsInput = ProposalCostAssumptions;
export const DEFAULT_PROPOSAL_COSTS: ProposalCostAssumptions = {
  vehicleMonthly: 6_200_000,
  perKm: 900,
  overheadPct: 8,
};

/** Rota belgesinde `internal` (yalnız servis firması). */
export const ProposalInternalSchema = z.object({
  costs: ProposalCostAssumptionsSchema.optional(),
  /** elle verilen aylık teklif fiyatı (kuruş); yoksa sözleşme birim fiyatıyla hesaplanır */
  monthlyPrice: z.number().int().min(0).nullish(),
});
export type ProposalInternal = z.infer<typeof ProposalInternalSchema>;

export const ProposalPlanStopInput = z.object({
  name: z.string().trim().min(2, 'Durak adı en az 2 harf olmalı.').max(80, 'Durak adı en fazla 80 harf olabilir.'),
  lat: z.number().min(35.5, 'Durak Türkiye sınırları dışında.').max(42.5, 'Durak Türkiye sınırları dışında.'),
  lng: z.number().min(25.5, 'Durak Türkiye sınırları dışında.').max(45, 'Durak Türkiye sınırları dışında.'),
});
export type ProposalPlanStopInput = z.infer<typeof ProposalPlanStopInput>;

export const PLAN_ROUTE_CHANGES = ['NEW', 'CHANGED', 'MERGED'] as const;
export type PlanRouteChange = (typeof PLAN_ROUTE_CHANGES)[number];

/** Önerilen bir hat: duraklar istemciden, geometri (çizgi, mesafe, süre, durak saatleri) sunucuda hesaplanır. */
export const ProposalPlanRouteInput = z.object({
  code: RouteCode,
  name: z.string().trim().min(3, 'Hat adı en az 3 harf olmalı.').max(80, 'Hat adı en fazla 80 harf olabilir.'),
  change: z.enum(PLAN_ROUTE_CHANGES),
  replaces: z.array(RouteCode).max(12).default([]),
  vehicles: z.number().int().min(1, 'En az bir araç gerekir.').max(20, 'Bir hatta en fazla 20 araç olabilir.'),
  stops: z
    .array(ProposalPlanStopInput)
    .min(2, 'Hat en az iki duraktan (başlangıç ve varış) oluşur.')
    .max(40, 'Bir hatta en fazla 40 durak olabilir.'),
});
export type ProposalPlanRouteInput = z.input<typeof ProposalPlanRouteInput>;

export const ProposalPlanInput = z.object({
  routes: z.array(ProposalPlanRouteInput).max(40, 'Bir teklifte en fazla 40 hat olabilir.').default([]),
  removed: z.array(RouteCode).max(40).default([]),
  monthlyPrice: z.number().int().min(0, 'Fiyat eksi olamaz.').max(100_000_000_000).nullish(),
});
export type ProposalPlanInput = z.input<typeof ProposalPlanInput>;

const Title = z
  .string()
  .trim()
  .min(5, 'Teklif adını en az 5 harfle yazın.')
  .max(120, 'Teklif adı en fazla 120 karakter olabilir.');
const Note = z.string().trim().max(1000, 'Değişiklik özeti en fazla 1000 karakter olabilir.');

export const ProposalCreateInput = z
  .object({
    contractId: z.string().min(1, 'Müşteri seçin.').max(64),
    title: Title,
    note: Note.optional(),
    validUntil: DateInput,
    source: z.enum(PROPOSAL_SOURCES).default('CURRENT'),
    scenarioId: z.string().max(64).optional(),
  })
  .refine((v) => v.source !== 'SCENARIO' || !!v.scenarioId, {
    message: 'Bir optimizasyon senaryosu seçin.',
    path: ['scenarioId'],
  });
export type ProposalCreateInput = z.input<typeof ProposalCreateInput>;

/** Taslağı güncelle (yalnız Taslak). */
export const ProposalUpdateInput = z
  .object({
    title: Title.optional(),
    note: Note.nullish(),
    validUntil: DateInput.optional(),
    plan: ProposalPlanInput.optional(),
  })
  .refine((v) => Object.values(v).some((x) => x !== undefined), { message: 'Değiştirilecek bir alan seçin.' });
export type ProposalUpdateInput = z.input<typeof ProposalUpdateInput>;

/** Kurumdaki teklifi güncelle ve yeniden gönder (İncelemede / Değişiklik istendi → İncelemede, yeni sürüm). */
export const ProposalReviseInput = z.object({
  title: Title.optional(),
  note: Note.nullish(),
  validUntil: DateInput.optional(),
  plan: ProposalPlanInput.optional(),
  changeNote: z
    .string()
    .trim()
    .min(10, 'Neyi değiştirdiğinizi en az 10 harfle yazın.')
    .max(1000, 'Açıklama en fazla 1000 karakter olabilir.'),
});
export type ProposalReviseInput = z.input<typeof ProposalReviseInput>;

export const ProposalSendInput = z.object({
  message: z.string().trim().max(500, 'Mesaj en fazla 500 karakter olabilir.').optional(),
});
export type ProposalSendInput = z.input<typeof ProposalSendInput>;

export const ProposalWithdrawInput = z.object({
  reason: z
    .string()
    .trim()
    .min(10, 'Geri çekme nedenini en az 10 harfle yazın.')
    .max(500, 'Açıklama en fazla 500 karakter olabilir.'),
});
export type ProposalWithdrawInput = z.input<typeof ProposalWithdrawInput>;

/** Plan tahmini: kaydetmeden ölçüt ve maliyet (editörde canlı). */
export const ProposalEstimateInput = z.object({ plan: ProposalPlanInput });
export type ProposalEstimateInput = z.input<typeof ProposalEstimateInput>;

/** Mevcut rota (yayındaki versiyon + etkin çizelge). */
export interface ProposalBaselineRoute {
  id: string;
  code: string;
  name: string;
  polyline: string;
  distanceM: number;
  durationS: number;
  vehicles: number;
  capacity: number;
  passengers: number;
  tripsPerMonth: number;
  /** yayındaki versiyonun durakları (planlama önizlemesinde değişmeyen bacakların yol çizgisi için kimlikli) */
  stops: (CompareStop & { id: string })[];
}

/** Önerilen hat + yeniden atamayla tahmini yük. */
export interface ProposalPlanRoute extends ProposedRoute {
  passengers: number;
  capacity: number;
  tripsPerMonth: number;
}

export interface ProposalPlan {
  routes: ProposalPlanRoute[];
  removed: string[];
  monthlyPrice: number | null;
}

/** Aylık iç maliyet ve kâr (kuruş). */
export interface ProposalCostSide {
  vehicles: number;
  km: number;
  trips: number;
  vehicleCost: number;
  kmCost: number;
  overhead: number;
  totalCost: number;
  /** kurumun ödediği aylık bedel */
  revenue: number;
  margin: number;
  /** kâr marjı (%); gelir yoksa null */
  marginPct: number | null;
}

export interface ProposalCostBreakdown {
  assumptions: ProposalCostAssumptions;
  current: ProposalCostSide;
  proposed: ProposalCostSide;
}

export type ProposalWarningCode = 'OVER_CAPACITY' | 'FAR_STOPS' | 'NO_CHANGES' | 'EXPIRED' | 'NO_SCHEDULE' | 'STALE';
export interface ProposalWarning {
  code: ProposalWarningCode;
  text: string;
  routeCode?: string;
}

export interface ProposalEstimate {
  plan: ProposalPlan;
  metrics: { current: ProposalMetrics; proposed: ProposalMetrics };
  costs: ProposalCostBreakdown;
  warnings: ProposalWarning[];
}

export interface ProposalHistoryItem {
  at: string;
  action: string;
  title: string;
  actor: string | null;
  detail: string | null;
  /** yalnız servis firmasına görünen kayıt (taslak, maliyet, paylaşım bağlantısı) */
  internal: boolean;
}

export interface ProposalWorkspace extends ProposalEstimate {
  id: string;
  title: string;
  status: ProposalStatus;
  revision: number;
  validUntil: string | null;
  source: ProposalSource;
  /** teklif bir optimizasyon senaryosundan doğduysa (onayda taslak versiyonlar senaryo ekranından açılır) */
  scenario: { id: string; label: string; strategy: string; runId: string } | null;
  contract: { id: string; number: string; pricingModel: string; unitPrice: number } | null;
  provider: CommercialParty;
  institution: CommercialParty;
  note: string | null;
  createdAt: string;
  decision: ProposalDetail['decision'];
  baseline: ProposalBaselineRoute[];
  history: ProposalHistoryItem[];
  shareLinks: { active: number; views: number };
  commentCount: number;
  openCommentCount: number;
  can: {
    edit: boolean;
    send: boolean;
    revise: boolean;
    withdraw: boolean;
    remove: boolean;
    share: boolean;
    costs: boolean;
  };
}

export interface ProposalContractOption {
  id: string;
  number: string;
  institution: CommercialParty;
  pricingModel: string;
  unitPrice: number;
  routes: number;
  metrics: ProposalMetrics;
}

export interface ProposalScenarioOption {
  id: string;
  label: string;
  strategy: string;
  recommendation: string | null;
  runId: string;
  createdAt: string;
  /** senaryonun kapsadığı müşteriler (sözleşme seçimine göre süzülür) */
  institutionIds: string[];
  metrics: Partial<ProposalMetrics>;
}

export interface ProposalNewOptions {
  contracts: ProposalContractOption[];
  scenarios: ProposalScenarioOption[];
  /** AI Optimizasyon bayrağı açık mı (kapalıysa senaryo seçeneği gizlenir) */
  scenariosEnabled: boolean;
  defaults: { validUntil: string; costs: ProposalCostAssumptions };
}

// ───────── güvenli paylaşım bağlantısı (C44) ─────────

export const SHARE_LINK_EXPIRY_DAYS = [7, 14, 30] as const;
export type ShareLinkExpiryDays = (typeof SHARE_LINK_EXPIRY_DAYS)[number];
export const ShareLinkCreateInput = z.object({
  expiresInDays: z.union([z.literal(7), z.literal(14), z.literal(30)], {
    error: 'Geçerlilik süresi 7, 14 veya 30 gün olabilir.',
  }),
});
export type ShareLinkCreateInput = z.input<typeof ShareLinkCreateInput>;

/** Herkese açık teklif görünümü: IP başına pencere (istek / saniye). */
export const SHARE_RATE_LIMIT = { perIp: 60, windowS: 600 } as const;

export type ShareLinkStatus = 'ACTIVE' | 'EXPIRED' | 'REVOKED';
export const SHARE_LINK_STATUS_LABELS: Record<ShareLinkStatus, string> = {
  ACTIVE: 'Etkin',
  EXPIRED: 'Süresi doldu',
  REVOKED: 'İptal edildi',
};

export interface ShareLinkItem {
  id: string;
  status: ShareLinkStatus;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
  viewCount: number;
}

export interface ShareLinkList {
  items: ShareLinkItem[];
  /** yeni bağlantı oluşturulabilir mi (taslak paylaşılamaz) */
  shareable: boolean;
  reason: string | null;
}

/** Ham anahtar yalnız oluşturma yanıtında bir kez döner; sunucuda yalnız SHA-256 özeti saklanır. */
export interface ShareLinkCreated {
  link: ShareLinkItem;
  url: string;
  token: string;
}

/** Oturumsuz paylaşım görünümü: iç maliyet, kişi adı ve yorum içermez; yolcu etkisi yalnız sayılar. */
export interface SharedProposalView {
  title: string;
  status: ProposalStatus;
  provider: { name: string };
  institution: { name: string };
  revision: number;
  validUntil: string | null;
  expiresAt: string;
  note: string | null;
  decision: { status: ProposalStatus; at: string } | null;
  metrics: { current: Partial<ProposalMetrics>; proposed: Partial<ProposalMetrics> };
  hasGeometry: boolean;
  routeCodes: string[];
  current: CompareRoute[];
  proposed: CompareRoute[];
  stopChanges: ProposalStopChanges | null;
  impact: ProposalImpact | null;
}

// ───────── faturalar (B23/B24, C55/C56) ─────────

export const INVOICE_STATUSES = ['DRAFT', 'ISSUED', 'PAID', 'OVERDUE'] as const;
export type InvoiceStatus = (typeof INVOICE_STATUSES)[number];
export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  DRAFT: 'Taslak',
  ISSUED: 'Kesildi',
  PAID: 'Ödendi',
  OVERDUE: 'Gecikti',
};

export const INVOICE_LINE_TYPES = ['BASE', 'EXTRA_KM', 'EXTRA_TRIP', 'DISCOUNT'] as const;
export type InvoiceLineType = (typeof INVOICE_LINE_TYPES)[number];
export const INVOICE_LINE_TYPE_LABELS: Record<InvoiceLineType, string> = {
  BASE: 'Servis bedeli',
  EXTRA_KM: 'Ek km',
  EXTRA_TRIP: 'Ek sefer',
  DISCOUNT: 'İndirim',
};

export const EXTRA_CHARGE_STATUSES = ['PENDING', 'APPROVED', 'INVOICED', 'REJECTED'] as const;
export type ExtraChargeStatus = (typeof EXTRA_CHARGE_STATUSES)[number];
export const EXTRA_CHARGE_STATUS_LABELS: Record<ExtraChargeStatus, string> = {
  PENDING: 'Onay bekliyor',
  APPROVED: 'Onaylandı',
  INVOICED: 'Faturalandı',
  REJECTED: 'Reddedildi',
};
export const EXTRA_CHARGE_TYPE_LABELS: Record<string, string> = { EXTRA_KM: 'Ek km', EXTRA_TRIP: 'Ek sefer' };

/** Kesilmiş ama vadesi geçmiş fatura "Gecikti" görünür (gece işi beklenmez). */
export function invoiceDisplayStatus(
  status: string,
  dueAt: string | Date | null,
  now: number = Date.now(),
): InvoiceStatus {
  if (status === 'ISSUED' && dueAt && new Date(dueAt).getTime() < now) return 'OVERDUE';
  return (INVOICE_STATUSES as readonly string[]).includes(status) ? (status as InvoiceStatus) : 'ISSUED';
}

export const INVOICE_FILTERS = ['open', ...INVOICE_STATUSES, 'all'] as const;
export type InvoiceFilter = (typeof INVOICE_FILTERS)[number];

export const InvoiceListQuery = z.object({
  status: z.enum(INVOICE_FILTERS).default('all'),
  year: z.coerce.number().int().min(2020).max(2100).optional(),
  q: z.string().trim().max(40).optional(),
  ...PageFields,
});
export type InvoiceListQuery = z.input<typeof InvoiceListQuery>;

export interface InvoiceRow {
  id: string;
  number: string;
  /** YYYY-MM */
  period: string;
  status: InvoiceStatus;
  provider: CommercialParty;
  institution: CommercialParty;
  contract: { id: string; number: string };
  subtotal: number;
  vat: number;
  total: number;
  issuedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
  /** gecikmiş faturada vadeden bu yana gün */
  daysOverdue: number | null;
}

export interface InvoiceSummary {
  /** kesilmiş, ödenmemiş toplam (kuruş, KDV dahil) */
  outstanding: number;
  outstandingCount: number;
  overdue: number;
  overdueCount: number;
  paidThisYear: number;
  nextDue: { id: string; number: string; dueAt: string; total: number } | null;
}

export interface InvoiceList extends Paged<InvoiceRow> {
  summary: InvoiceSummary;
  years: number[];
}

export interface InvoiceLineItem {
  id: string;
  type: InvoiceLineType;
  description: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  trip: { id: string; date: string; routeCode: string; direction: Direction } | null;
}

export interface InvoiceParty extends CommercialParty {
  legalName: string | null;
  taxNo: string | null;
  address: string | null;
  city: string;
}

export interface ExtraChargeItem {
  id: string;
  type: string;
  quantity: number;
  unitPrice: number;
  amount: number;
  note: string | null;
  status: ExtraChargeStatus;
  createdAt: string;
  provider: CommercialParty;
  institution: CommercialParty;
  trip: { id: string; date: string; routeCode: string; direction: Direction } | null;
}

export interface InvoiceDetail extends InvoiceRow {
  seller: InvoiceParty;
  buyer: InvoiceParty;
  contractTerms: {
    number: string;
    pricingModel: string;
    unitPrice: number;
    extraKmPrice: number;
    extraTripPrice: number;
  };
  lines: InvoiceLineItem[];
  /** KDV oranı (%) — ara toplamdan hesaplanır */
  vatRate: number;
  /** dönem içinde açılan ek ücret kayıtları (ek km / ek sefer) */
  extraCharges: ExtraChargeItem[];
}

export const ExtraChargeQuery = z.object({
  status: z.enum(['open', ...EXTRA_CHARGE_STATUSES, 'all']).default('open'),
  ...PageFields,
});
export type ExtraChargeQuery = z.input<typeof ExtraChargeQuery>;

export interface ExtraChargeList extends Paged<ExtraChargeItem> {
  /** açık (onay bekleyen + onaylanmış, faturalanmamış) toplam (kuruş) */
  openTotal: number;
}
